import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { linkOptions } from '@/constants/options';
import { isSupabaseStorageUrl, openStoredFile } from '@/helpers/storage.helper';
import { cn } from '@/lib/ui/utils';
import { useLinkClickStore } from '@/stores/stages/useLinkClickStore';
import { useAimeiChatStore } from '@/stores/useAimeiChatStore';
import { StagePrompt } from '@/types/general-settings/stage.prompt.type';

interface ReferButtonProps {
  type: string;
  link?: string;
  isUpload?: boolean;
  className?: string;
  openUploadDialog?: () => void;
  stageItemId?: string;
  stagePrompt?: StagePrompt;
}

export const ReferButton = ({
  type,
  link,
  isUpload = false,
  className,
  openUploadDialog,
  stageItemId,
  stagePrompt,
}: ReferButtonProps) => {
  const { addClickedLink } = useLinkClickStore();
  const { setPromptContextAndOpen, setPromptContext } = useAimeiChatStore();
  const option = linkOptions.find((option) => option.value === type);
  const { label, color } = option || { label: '', color: '' };

  const sendPromptToChat = (prompt: StagePrompt) => {
    // Set the prompt context in the store and ensure chat is opened
    // Only use the title, not the full prompt text
    const promptText = prompt.title || '';
    setPromptContextAndOpen(promptText);
  };

  const handleClick = () => {
    if (isUpload && openUploadDialog) {
      openUploadDialog();
      // Track the upload dialog opening as equivalent to clicking a link
      if (stageItemId) {
        addClickedLink(stageItemId);
      }
    } else if (type === 'ask-aimei') {
      // Handle ask-aimei button clicks
      if (stagePrompt && (stagePrompt.title || stagePrompt.prompt)) {
        sendPromptToChat(stagePrompt);
      } else {
        setPromptContext(null);
      }

      if (stageItemId) {
        addClickedLink(stageItemId);
      }
    }

    // Track the link click if stageItemId is provided and it's not an upload
    if (stageItemId && !isUpload) {
      addClickedLink(stageItemId);
    }
  };

  const handleLinkClick = () => {
    // Track the link click for external/internal links
    if (stageItemId) {
      addClickedLink(stageItemId);
    }
  };

  // Stored files (private bucket) need a signed URL before opening. Already
  // signed or external links keep going through the plain link below.
  const isStoredFile = type !== 'link-to-tab' && isSupabaseStorageUrl(link);

  return (
    <Button
      type="button"
      style={{
        color,
        borderColor: color,
        backgroundColor: `${color}20`,
      }}
      asChild
      className={cn('cursor-pointer rounded-full border font-bold', className)}
      onClick={handleClick}
    >
      {link && isStoredFile ? (
        <button
          type="button"
          onClick={() => {
            handleLinkClick();
            openStoredFile(link);
          }}
        >
          {label}
        </button>
      ) : link ? (
        <Link
          to={
            type === 'link-to-tab'
              ? link === 'onboarding' || link === 'discovery'
                ? `/dashboard/profile?tab=general`
                : `/dashboard/${link}`
              : link
          }
          target="_blank"
          onClick={handleLinkClick}
        >
          {label}
        </Link>
      ) : (
        <span className="as-body-03 text-zinc-800">{label}</span>
      )}
    </Button>
  );
};
